import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { 
  ArrowRight, 
  ArrowLeft,
  Briefcase,
  Code,
  Users,
  Loader2,
  CheckCircle2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function RoleSelect() {
  const { user, refreshUser, loading: authLoading } = useAuth();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/app/login');
      return;
    }
    if (user) {
      fetchRoles();
    }
  }, [user, authLoading, navigate]);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API_URL}/api/app/roles`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.roles || []);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectRole = async () => {
    if (!selectedRole) return;

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/app/student/select-role?role_id=${selectedRole}`, {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        await refreshUser();
        navigate('/app/action-plan');
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to select role');
      }
    } catch (error) {
      console.error('Failed to select role:', error);
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getRoleIcon = (slug) => {
    switch (slug) {
      case 'junior-qa': return Code;
      case 'business-operations': return Briefcase;
      case 'inside-sales': return Users;
      default: return Briefcase;
    }
  };

  const formatSalary = (amount) => {
    if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    }
    return `₹${(amount / 1000).toFixed(0)}K`;
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/app/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">Back</span>
          </Link>
          <div className="bg-white border border-slate-200 px-2 py-1 rounded-lg">
            <img 
              src="/logo-elevate-careers.webp" 
              alt="Elevate Careers" 
              className="h-6 w-auto"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 pb-24">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">
            Choose Your Career Path
          </h1>
          <p className="text-slate-600">
            Select a target role to get your personalized 7-day action plan
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Role Cards */}
        <div className="space-y-4">
          {roles.map((role) => {
            const Icon = getRoleIcon(role.slug);
            const isSelected = selectedRole === role.id;

            return (
              <button
                key={role.id}
                onClick={() => setSelectedRole(role.id)}
                className={`w-full text-left p-5 rounded-2xl border-2 transition-all ${
                  isSelected 
                    ? 'border-teal-500 bg-teal-50 shadow-md' 
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                }`}
                data-testid={`role-card-${role.slug}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${
                    isSelected ? 'bg-teal-100' : 'bg-slate-100'
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      isSelected ? 'text-teal-600' : 'text-slate-500'
                    }`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`font-semibold ${
                        isSelected ? 'text-[#0b1f4d]' : 'text-slate-900'
                      }`}>
                        {role.name}
                      </h3>
                      {isSelected && (
                        <CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-sm text-slate-600 mt-1">
                      {role.description}
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <span className="px-2 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
                        {formatSalary(role.salary_range?.min)} - {formatSalary(role.salary_range?.max)} / year
                      </span>
                      <span className="px-2 py-1 bg-slate-100 rounded-lg text-xs text-slate-600">
                        Entry Level
                      </span>
                    </div>
                    {/* Competencies */}
                    <div className="mt-3">
                      <p className="text-xs font-medium text-slate-500 mb-1">Key skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {role.competencies?.slice(0, 3).map((comp, idx) => (
                          <span 
                            key={idx} 
                            className={`px-2 py-0.5 rounded text-xs ${
                              isSelected 
                                ? 'bg-teal-100 text-teal-700' 
                                : 'bg-slate-50 text-slate-600'
                            }`}
                          >
                            {comp.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      {/* Fixed Bottom CTA */}
      {selectedRole && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={handleSelectRole}
              disabled={submitting}
              className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium"
              data-testid="select-role-submit"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Creating your plan...
                </>
              ) : (
                <>
                  Get My 7-Day Plan
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
